import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Avatar,
  Button,
  Spinner,
} from '@genie-ui/react';
import PersonIcon from '@mui/icons-material/Person';
import SnackbarComponent from 'components/atoms/snackbarcomponent';
import './team-management-data.scss';
import CYBER_CONSTANT from 'constants/CyberConstants';
import { resendInvite } from 'components/pages/cyber/api';
import Constants from 'constants/Constants';
import { useSelector } from 'react-redux';
import TeamManagementMenu from 'components/molecules/teammanagementmenu';

const TeamManagementDataConstants = {
  PENDING: 'Pending',
  EXPIRED: 'Expired',
  ACTIVE: 'Active',
  RESEND_INVITE: 'Re-send Email',
  REMOVE_USER: 'Remove User',
  CANCEL: 'Cancel',
  OPTIONS: ['Manager', 'Contributor', 'Remove User'],
};

function TeamManagementData({
  list,
  onRemoveTeamMember,
  onUpdateRole,
  showInvitationVerification,
  dashboardId,
}) {
  const {
    actisData,
  } = useSelector((state) => state.cyber);
  const { ROLES, RESPONSES } = CYBER_CONSTANT.TEAM_MANAGEMENT;
  const {
    name = '',
    email,
    status,
    userType,
  } = list;
  const [currentRole, setCurrentRole] = useState(userType);
  const [removeUserPrompt, setRemoveUserPrompt] = useState(false);
  const isPending = status?.toLowerCase() === TeamManagementDataConstants.PENDING?.toLowerCase();
  const isExpired = status?.toLowerCase() === TeamManagementDataConstants.EXPIRED?.toLowerCase();

  const [successSnackbar, setSuccessSnackbar] = useState('');
  const [failureSnackbar, setFailureSnackbar] = useState('');

  const [resendTeamInviteLoading, setResendTeamInviteLoading] = useState(false);

  const closeSuccessSnackbar = () => {
    setSuccessSnackbar('');
  };

  const closeFailureSnackbar = () => {
    setFailureSnackbar('');
  };

  const onRoleChange = (newRole) => {
    if (newRole === TeamManagementDataConstants.REMOVE_USER) setRemoveUserPrompt(true);
    else if (newRole !== userType) {
      setCurrentRole(newRole);
      onUpdateRole({ email, role: newRole });
    }
  };

  const cancelRemoveUser = () => setRemoveUserPrompt(false);
  const removeUser = () => {
    onRemoveTeamMember(email, userType);
  };

  const resendTeamInvite = () => {
    setResendTeamInviteLoading(true);
    resendInvite({ email, dashboardId })?.then((response) => {
      if (response?.body?.systemMessage?.success) {
        setSuccessSnackbar(RESPONSES.RESEND_INVITE_SUCCESS);
      } else {
        setFailureSnackbar(RESPONSES.RESEND_INVITE_ERROR);
      }
    }).finally(() => {
      setResendTeamInviteLoading(false);
    });
    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.RESENDS_INVITE,
        activityMeta: `${CYBER_CONSTANT.ACTIS.RESENDS_INVITE.activityMeta} ${userType}`,
        activityCategory: userType,
        activityValue: email,
        ...actisData,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const renderCTA = () => {
    if (resendTeamInviteLoading) {
      return <Spinner />;
    }

    if (userType?.toLowerCase() === ROLES.OWNER?.toLowerCase()) {
      return (
        <Typography className="team-management-data__owner">Owner</Typography>
      );
    }

    if (removeUserPrompt) {
      return (
        <div className="team-management-data__buttonSection">
          <Button
            variant="primary"
            onClick={cancelRemoveUser}
            className="team-management-data__buttonSection__cancel"
          >
            {TeamManagementDataConstants.CANCEL}
          </Button>
          <Button
            variant="ghost"
            onClick={removeUser}
            className="team-management-data__buttonSection__remove"
          >
            {TeamManagementDataConstants.REMOVE_USER}
          </Button>
        </div>
      );
    }

    return (
      <div className="team-management-data__buttonSection">
        {isPending && (
        <Button
          size="small"
          variant="ghost"
          className="team-management-data__buttonSection__resend"
          onClick={resendTeamInvite}
          disabled={showInvitationVerification}
        >
          {TeamManagementDataConstants.RESEND_INVITE}
        </Button>
        )}
        <TeamManagementMenu
          options={TeamManagementDataConstants.OPTIONS}
          className="team-management-data__dropdown"
          role={currentRole}
          setRole={onRoleChange}
          disabled={showInvitationVerification}
        />
      </div>
    );
  };

  const getIcon = () => {
    if (name) {
      return `${name?.charAt() || ''}${name?.split(' ')?.[1]?.charAt() || ''}`;
    }
    return <PersonIcon />;
  };

  const getLabel = () => {
    if (isPending) {
      return TeamManagementDataConstants.PENDING;
    }
    if (isExpired) {
      return TeamManagementDataConstants.EXPIRED;
    }
    if (name) {
      return name;
    }
    return '';
  };

  return (
    <div className="team-management-data">
      <div className="team-management-data__detailsSection">
        {isPending || isExpired ? (
          <Avatar className={`team-management-data__detailsSection__personIcon team-management-data__detailsSection__personIcon__${status}`}>
            <PersonIcon />
          </Avatar>
        ) : (
          <Avatar className="team-management-data__detailsSection__icon">
            {getIcon()}
          </Avatar>
        )}

        <div className="team-management-data__detailsSection__info">
          <Typography
            className={`${isPending || isExpired
              ? 'team-management-data__detailsSection__disable'
              : 'team-management-data__detailsSection__name'
            }`}
          >
            {getLabel()}
          </Typography>
          <Typography
            className={`team-management-data__detailsSection__email ${isPending
              ? 'team-management-data__detailsSection__disable'
              : ''
            }`}
          >
            {email}
          </Typography>
        </div>
      </div>
      <div>
        {renderCTA()}
      </div>
      <SnackbarComponent
        msg={failureSnackbar}
        onClose={closeFailureSnackbar}
        variant={Constants.SNACKBAR.ERROR}
        open={failureSnackbar.length}
        anchorOrigin={{
          vertical: 'bottom', horizontal: 'center',
        }}
      />
      <SnackbarComponent
        msg={successSnackbar}
        onClose={closeSuccessSnackbar}
        variant={Constants.SNACKBAR.SUCCESS}
        open={successSnackbar?.length}
        anchorOrigin={{
          vertical: 'bottom', horizontal: 'center',
        }}
      />
    </div>
  );
}

TeamManagementData.propTypes = {
  list: PropTypes.objectOf(PropTypes.string).isRequired,
  onRemoveTeamMember: PropTypes.func.isRequired,
  onUpdateRole: PropTypes.func.isRequired,
  showInvitationVerification: PropTypes.bool.isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default TeamManagementData;
